import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { CustomerStore } from '../../shared/stores/customer.store';
import { TenantConfigStore } from '../../shared/stores/tenant-config.store';
import { PreviewModeService } from '../services/preview-mode.service';

export const PREVIEW_STORAGE_KEY = '__portal_preview';

/**
 * Activates the portal preview route without auth or bootstrap guards.
 *
 * The admin action bar writes the effective (draft → published) portal config
 * to `localStorage[PREVIEW_STORAGE_KEY]` before opening the preview tab.
 * This guard:
 *  1. Validates the preview payload exists.
 *  2. Primes CustomerStore with the customerId from the URL.
 *  3. Calls PortalConfigStore.load() — which reads and removes the payload
 *     synchronously via `of(config)` inside PortalConfigService.load().
 *  4. Returns `true` — no login required (AC#96759-6).
 *
 * If no preview payload exists, redirects to /access-id.
 */
export const previewGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const customerStore = inject(CustomerStore);
  const portalConfigStore = inject(TenantConfigStore);
  const previewModeService = inject(PreviewModeService);
  const router = inject(Router);

  // Prefer own paramMap (Angular inherits from componentless parents);
  // fall back to parent snapshot for robustness.
  const customerId =
    route.paramMap.get('customerId') ??
    route.parent?.paramMap.get('customerId') ??
    null;
  if (!customerId) {
    return router.createUrlTree(['/access-id']);
  }

  // Guard only checks existence — PortalConfigService.load() consumes + removes the key.
  // Fallback: if localStorage is empty (Chrome 115+ partitions third-party iframe storage),
  // decode the preview payload from the URL hash written by the admin store.
  // The hash carries a single-use token — once consumed, copied URLs are rejected.
  if (!localStorage.getItem(PREVIEW_STORAGE_KEY)) {
    const rawHash = globalThis.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(rawHash);
    const token = params.get('token');
    const previewParam = params.get('preview');
    if (token && previewParam) {
      const usedKey = `__portal_preview_used_${token}`;
      if (localStorage.getItem(usedKey)) {
        // Token already consumed — URL was copied into another tab.
        return router.createUrlTree(['/access-id']);
      }
      try {
        const binary = atob(previewParam);
        const bytes = Uint8Array.from(binary, c => c.codePointAt(0) ?? 0);
        const decoded = new TextDecoder().decode(bytes);
        localStorage.setItem(PREVIEW_STORAGE_KEY, decoded);
        // Mark token consumed so any subsequent use of the same URL is rejected.
        localStorage.setItem(usedKey, '1');
      } catch { /* malformed hash — fall through to /access-id redirect */ }
    }
  }

  if (!localStorage.getItem(PREVIEW_STORAGE_KEY)) {
    return router.createUrlTree(['/access-id']);
  }

  try {
    // Mark this tab as preview so downstream guards (auth, customerBootstrap)
    // and stores (CampaignAssignmentsStore) skip API calls (AC#96759-6/8/12).
    previewModeService.enable();

    // Prime CustomerStore so downstream services that read customerId work.
    customerStore.setCustomerId(customerId);
    customerStore.setSource('path');

    // Synchronous: PortalConfigService.load() returns of(config) when the
    // localStorage key exists, so patchState fires before this returns.
    portalConfigStore.load();

    return true;
  } catch {
    return router.createUrlTree(['/access-id']);
  }
};
